"use client";

import { useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { SquarePen, Check, Lock, AlertTriangle } from "lucide-react";
import { useToast } from "@/app/_module/hooks/use-toast";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";
import { verifyMFAForAction, resendSMSForAction } from "@/app/_module/services/auth";

import { useUpdateAgencyUserRole } from "../../hooks/use-hook";
import type { AgencyUserWithAgency } from "../../types/types";

interface EditUserRoleProps {
  agencyUser: AgencyUserWithAgency;
  agencyId: string;
  trigger?: React.ReactNode;
}

export const ROLE_OPTIONS = [
  { value: "agent", label: "Agent", requiresFullFledged: false },
  { value: "admin", label: "Agency Admin", requiresFullFledged: true },
] as const;

const EditUserRole = function({ agencyUser, agencyId, trigger }: EditUserRoleProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const updateUserRoleMutation = useUpdateAgencyUserRole();

  // Double confirmation + MFA state for admin promotion
  const [showFirstConfirm, setShowFirstConfirm] = useState(false);
  const [showSecondConfirm, setShowSecondConfirm] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [pendingPromotion, setPendingPromotion] = useState<{ newRole: "agent" | "admin" } | null>(null);

  // Check if user has full_fledged or super_agent account type
  const userAccountType = agencyUser.user.accountType?.toLowerCase();
  const isFullFledged = userAccountType === 'full_fledged' || userAccountType === 'super_agent';

  const resetPromotionFlow = () => {
    setShowFirstConfirm(false);
    setShowSecondConfirm(false);
    setShowMFAModal(false);
    setPendingPromotion(null);
  };

  const handleRoleChange = async (newRole: "agent" | "admin", requiresFullFledged: boolean) => {
    // Check if trying to set a role that requires full_fledged but user doesn't have it
    if (requiresFullFledged && !isFullFledged) {
      toast({
        title: "Full-Fledged Plan Required",
        description: "This user needs to upgrade to a Full-Fledged plan before being promoted to Agency Admin.",
        variant: "destructive",
      });
      return;
    }

    if (newRole === agencyUser.role) {
      setOpen(false);
      return;
    }

    // If promoting to admin, start double confirmation + MFA flow
    if (newRole === 'admin') {
      setOpen(false);
      setPendingPromotion({ newRole });
      setShowFirstConfirm(true);
      return;
    }

    // For non-admin role changes, proceed directly
    try {
      await updateUserRoleMutation.mutateAsync({
        agencyId,
        data: {
          userId: agencyUser.userId,
          oldRole: agencyUser.role,
          newRole,
        },
      });
      setOpen(false);
    } catch (error) {
      // Error is handled by the mutation hook
    }
  };

  const handleFirstConfirm = () => {
    setShowFirstConfirm(false);
    setShowSecondConfirm(true);
  };

  const handleSecondConfirm = () => {
    setShowSecondConfirm(false);
    setShowMFAModal(true);
  };

  const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
    try {
      return await verifyMFAForAction(code, method);
    } catch {
      return false;
    }
  };

  const handleMFAVerified = async () => {
    if (!pendingPromotion) return;

    try {
      await updateUserRoleMutation.mutateAsync({
        agencyId,
        data: {
          userId: agencyUser.userId,
          oldRole: agencyUser.role,
          newRole: pendingPromotion.newRole,
        },
      });
      toast({
        title: "Role Updated",
        description: `${agencyUser.user.name} has been promoted to Agency Admin.`,
      });
    } catch (error) {
      // Error is handled by the mutation hook
    } finally {
      resetPromotionFlow();
    }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              disabled={updateUserRoleMutation.isPending}
            >
              <SquarePen className="h-3 w-3" />
              <span className="sr-only">Edit role</span>
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Change Role</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {ROLE_OPTIONS.map((role) => {
            const isLocked = role.requiresFullFledged && !isFullFledged;
            return (
              <DropdownMenuItem
                key={role.value}
                onClick={() => handleRoleChange(role.value, role.requiresFullFledged)}
                disabled={updateUserRoleMutation.isPending}
                className={`cursor-pointer ${isLocked ? 'text-muted-foreground' : ''}`}
              >
                <div className="flex items-center justify-between w-full">
                  <span>{role.label}</span>
                  <div className="flex items-center gap-1">
                    {isLocked && (
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    )}
                    {agencyUser.role === role.value && (
                      <Check className="h-4 w-4 ml-1 text-green-600" />
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* First Confirmation Dialog */}
      <Dialog open={showFirstConfirm} onOpenChange={(v) => { if (!v) resetPromotionFlow(); }}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Promote to Agency Admin?
            </DialogTitle>
            <DialogDescription>
              Promote <strong>{agencyUser.user.name}</strong> to Agency Admin? They will be able to assign leads and manage the agency, but will <span className="text-red-500 font-semibold">NOT</span> be able to make calls or send SMS.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={resetPromotionFlow}>
              Cancel
            </Button>
            <Button onClick={handleFirstConfirm}>
              Continue
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Second Confirmation Dialog */}
      <Dialog open={showSecondConfirm} onOpenChange={(v) => { if (!v) resetPromotionFlow(); }}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirm Promotion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to promote <strong>{agencyUser.user.name}</strong> to Agency Admin? This action requires MFA verification to complete. Agency Admins have elevated permissions. Calling and SMS are disabled by default but can be enabled in Agency Center.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={resetPromotionFlow}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSecondConfirm}>
              Verify with MFA
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* MFA Verification Modal */}
      <SimplifiedMFAModal
        isOpen={showMFAModal}
        onClose={resetPromotionFlow}
        onVerified={handleMFAVerified}
        onVerify={handleMFAVerify}
        onSendSMS={resendSMSForAction}
        title="Verify to Promote User"
        description={`Enter your MFA code to promote ${agencyUser.user.name} to Agency Admin.`}
      />
    </>
  );
}

export default EditUserRole;
